import {
    useEffect,
    useRef
} from 'react';

/* ========================================================================= */
/* Constant(s) */
/* ========================================================================= */

/** Pointer travel in pixels before a press is treated as a drag rather than a click */
const DRAG_TOLERANCE = 3;

/** Delay before snapping is restored in browsers without scrollend event */
const SCROLL_SETTLE_DELAY = 500;

/* ========================================================================= */
/* Helper(s) */
/* ========================================================================= */

/**
 * Get the scroll positions at which each slide is snapped into place
 * @param {HTMLUListElement} track
 * @return {number[]}
 */
const getSlideOffsets = (track) => {
    const trackLeft = track.getBoundingClientRect().left;
    const slideElements = track.querySelectorAll('[data-carousel-slide]');

    return Array.from(slideElements, (slide) => {
        return (slide.getBoundingClientRect().left - trackLeft + track.scrollLeft);
    });
};

/**
 * The index of the slide snapped closest to a given scroll position
 * @param {number[]} offsets
 * @param {number} scrollLeft
 * @return {number}
 */
const getSlideNearestIndex = (offsets, scrollLeft) => {
    return offsets.reduce(
        (nearest, offset, index) => {
            if (Math.abs(offset - scrollLeft) < Math.abs(offsets[nearest] - scrollLeft)) {
                return index;
            } else {
                return nearest;
            }
        },
        0
    );
};

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * @hook useCarouselSwipe
 * @type {import('$types/components/hooks/carousel.d.ts').UseCarouselSwipe}
 *
 * @example
 *  const { trackRef } = useCarouselSwipe();
 *  <ul ref={trackRef}><li data-carousel-slide /></ul>
 */
export const useCarouselSwipe = ({ threshold = 40 } = {}) => {
    const trackRef = useRef(null);

    useEffect(() => {
        if (!trackRef.current) {
            return;
        }

        const track = trackRef.current;

        let cancelRestore = null;
        let drag = null;
        let suppressClick = false;

        const disableSnapping = () => {
            cancelRestore?.();
            cancelRestore = null;
            track.style.scrollSnapType = '';
            track.style.userSelect = 'none';
        };

        const restoreSnappingOnSettle = () => {
            const restore = () => {
                cancelRestore = null;
                track.style.scrollSnapType = 'x mandatory';
                track.style.userSelect = '';
            };

            if ('onscrollend' in window) {
                track.addEventListener('scrollend', restore, { once: true });
                cancelRestore = () => track.removeEventListener('scrollend', restore);
            } else {
                const timeout = setTimeout(restore, SCROLL_SETTLE_DELAY);
                cancelRestore = () => clearTimeout(timeout);
            }
        };

        /** @param {number} left */
        const scrollToPosition = (left) => {
            if (Math.abs(left - track.scrollLeft) <= 10) {
                track.style.scrollSnapType = 'x mandatory';
                track.style.userSelect = '';
                return;
            }

            track.scrollTo({
                behavior: 'smooth',
                left
            });

            restoreSnappingOnSettle();
        };

        const endDrag = () => {
            if (!drag) {
                return;
            }

            const {
                distance,
                isDragging,
                offsets,
                startScrollLeft
            } = drag;

            drag = null;

            if (!isDragging) {
                return;
            }

            /* The pointer has moved, so any click it produces is part of the drag */
            suppressClick = true;

            const step = Math.abs(distance) >= threshold ? -Math.sign(distance) : 0;
            const index = getSlideNearestIndex(offsets, startScrollLeft) + step;
            scrollToPosition(offsets[index]);
        };

        /** @param {PointerEvent} event */
        const handlePointerDown = (event) => {
            suppressClick = false;

            if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) {
                return;
            }

            drag = {
                distance: 0,
                isDragging: false,
                offsets: getSlideOffsets(track),
                startScrollLeft: track.scrollLeft,
                startX: event.clientX
            };
        };

        /** @param {PointerEvent} event */
        const handlePointerMove = (event) => {
            if (!drag) {
                return;
            }

            /* No mouse button pressed */
            if (event.buttons === 0) {
                endDrag();
                return;
            }

            drag.distance = event.clientX - drag.startX;

            if (!drag.isDragging) {
                if (Math.abs(drag.distance) < DRAG_TOLERANCE) {
                    return;
                }

                drag.isDragging = true;
                disableSnapping();
            }

            /* Stops the pointer selecting text or dragging links out of the track */
            event.preventDefault();
            track.scrollLeft = drag.startScrollLeft - drag.distance;
        };

        const handlePointerEnd = () => {
            if (!drag) {
                return;
            }

            endDrag();
        };

        /** @param {MouseEvent} event */
        const handleClick = (event) => {
            if (suppressClick) {
                suppressClick = false;
                event.preventDefault();
                event.stopPropagation();
            }
        };

        /** @param {DragEvent} event */
        const handleDragStart = (event) => {
            if (drag?.isDragging) {
                event.preventDefault();
            }
        };

        // activate styles
        track.style.display = 'flex';
        track.style.overflowX = 'auto';
        track.style.scrollSnapType = 'x mandatory';
        track.style.webkitUserDrag = 'none';
        track.style.webkitUserSelect = 'none';

        const slideElements = track.querySelectorAll('[data-carousel-slide]');
        slideElements.forEach((slide) => {
            slide.style.flexShrink = '0';
            slide.style.listStyle = 'none';
            slide.style.scrollbarWidth = 'none';
            slide.style.scrollSnapAlign = 'start';
            slide.style.scrollSnapStop = 'always';
        });

        // activate event handlers
        track.addEventListener('click', handleClick, true);
        track.addEventListener('dragstart', handleDragStart);
        track.addEventListener('pointercancel', handlePointerEnd);
        track.addEventListener('pointerdown', handlePointerDown);
        track.addEventListener('pointermove', handlePointerMove);
        track.addEventListener('pointerup', handlePointerEnd);

        window.addEventListener('blur', endDrag);

        return () => {
            cancelRestore?.();

            // deactivate styles
            track.style.display = '';
            track.style.overflowX = '';
            track.style.scrollSnapType = '';
            track.style.webkitUserDrag = '';
            track.style.webkitUserSelect = '';

            const slideElements = track.querySelectorAll('[data-carousel-slide]');
            slideElements.forEach((slide) => {
                slide.style.flexShrink = '';
                slide.style.listStyle = '';
                slide.style.scrollbarWidth = '';
                slide.style.scrollSnapAlign = '';
                slide.style.scrollSnapStop = '';
            });

            // deactivate event handlers
            track.removeEventListener('click', handleClick, true);
            track.removeEventListener('dragstart', handleDragStart);
            track.removeEventListener('pointercancel', handlePointerEnd);
            track.removeEventListener('pointerdown', handlePointerDown);
            track.removeEventListener('pointermove', handlePointerMove);
            track.removeEventListener('pointerup', handlePointerEnd);

            window.removeEventListener('blur', endDrag);
        };
    }, [ threshold ]);

    return { trackRef };
};
